import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Tag,
  Typography,
  Space,
  Spin,
  Image,
  Row,
  Col,
  Tooltip,
  message,
  Checkbox,
  Badge,
  Input,
  InputNumber,
  Segmented,
  Popconfirm,
} from 'antd';
import {
  ArrowLeftOutlined,
  PictureOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  FilterOutlined,
  RightOutlined,
  DownOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CheckSquareOutlined,
  BorderOutlined,
  ExportOutlined,
  DeleteOutlined,
  EditOutlined,
  CloseOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  CodeOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import CaptionEditor from './CaptionEditor';
import ExportDialog from './ExportDialog';
import FullscreenViewer from './FullscreenViewer';

const { Title, Text } = Typography;

interface Dataset {
  id: number;
  name: string;
  buckets: string[];
  type: string;
}

interface ImageData {
  id: number;
  bucket_name: string;
  region: string;
  s3_url: string;
  thumbnail_uri: string;
  original_width: number;
  original_height: number;
  thumbnail_width: number;
  thumbnail_height: number;
  file_size_formatted: string;
  size_category: string;
  caption_summary?: string;
  rotation?: number;
}

interface Pagination {
  cursor: number | null;
  limit: number;
  total: number;
  hasMore: boolean;
}

interface SizeCategory {
  value: string;
  label: string;
}

interface SizeStats {
  [key: string]: number;
}

interface AspectRatioStat {
  aspect_ratio: string;
  count: number;
}

interface FilterOption {
  value: string;
  label: string;
}

interface FilterConfig {
  label: string;
  values: FilterOption[];
  type: string;
  counts: { [key: string]: number };
}

interface CaptionFilters {
  datasetType: string;
  filters: { [key: string]: FilterConfig };
}

interface NumberFilter {
  min: number | null;
  max: number | null;
}

type FilterMode = 'include' | 'exclude';

interface CaptionFilterState {
  dropdowns: { [key: string]: string[] };
  booleans: { [key: string]: boolean | null };
  numbers: { [key: string]: NumberFilter };
  text: { [key: string]: string };
  modes: { [key: string]: FilterMode };
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

const SIZE_CATEGORIES: SizeCategory[] = [
  { value: 'extra_tiny', label: 'XTiny' },
  { value: 'tiny', label: 'Tiny' },
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
  { value: 'extra_large', label: 'XL' },
];

const DatasetManager: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [dataset, setDataset] = useState<Dataset | null>(null);
  const [images, setImages] = useState<ImageData[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(true);

  // Filter state
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [sizeStats, setSizeStats] = useState<SizeStats>({});
  const [selectedAspectRatios, setSelectedAspectRatios] = useState<string[]>([]);
  const [aspectRatioStats, setAspectRatioStats] = useState<AspectRatioStat[]>([]);
  const [expandedFilters, setExpandedFilters] = useState<string[]>(['size', 'ratio']);
  const [captionFilters, setCaptionFilters] = useState<CaptionFilters | null>(null);
  const [selectedCaptionFilters, setSelectedCaptionFilters] = useState<CaptionFilterState>({
    dropdowns: {},
    booleans: {},
    numbers: {},
    text: {},
    modes: {}
  });
  const [selectedModerationStatus, setSelectedModerationStatus] = useState<string[]>([]);
  const [allApproveTags, setAllApproveTags] = useState<string[]>([]);
  const [allDeclineTags, setAllDeclineTags] = useState<string[]>([]);
  const [selectedApproveTags, setSelectedApproveTags] = useState<string[]>([]);
  const [selectedDeclineTags, setSelectedDeclineTags] = useState<string[]>([]);
  const [filterPanelOpen, setFilterPanelOpen] = useState(true);

  // Selection state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedImages, setSelectedImages] = useState<Set<number>>(new Set());

  // View mode
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Caption editor state
  const [editingImageId, setEditingImageId] = useState<number | null>(null);

  // Fullscreen viewer state
  const [fullscreenImage, setFullscreenImage] = useState<ImageData | null>(null);

  // Export dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  // Deleting state
  const [deleting, setDeleting] = useState(false);

  // Refreshing cache state
  const [refreshingCache, setRefreshingCache] = useState(false);

  // Right panel mode: 'filters' or 'json'
  const [rightPanelMode, setRightPanelMode] = useState<'filters' | 'json'>('filters');

  // Selected image for JSON view
  const [selectedImageForJson, setSelectedImageForJson] = useState<ImageData | null>(null);
  const [jsonData, setJsonData] = useState<any>(null);
  const [jsonLoading, setJsonLoading] = useState(false);

  // Right panel resize state
  const [rightPanelWidth, setRightPanelWidth] = useState(260);
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    fetchDataset();
    fetchSizeStats();
    fetchAspectRatioStats();
    fetchCaptionFilters();
    fetchTags();
  }, [id]);

  useEffect(() => {
    if (dataset) {
      resetAndFetch();
    }
  }, [dataset, selectedSizes, selectedAspectRatios, selectedCaptionFilters, selectedModerationStatus, selectedApproveTags, selectedDeclineTags]);

  const resetAndFetch = () => {
    setImages([]);
    setCursor(null);
    setHasMore(true);
    setSelectedImages(new Set());
    fetchImages(null, true);
  };

  const fetchDataset = async () => {
    try {
      const response = await axios.get(`${API_URL}/datasets/${id}`);
      setDataset(response.data);
    } catch (error) {
      console.error('Failed to fetch dataset:', error);
      message.error('Failed to load dataset');
      navigate('/datasets');
    }
  };

  const fetchSizeStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/datasets/${id}/size-stats`);
      setSizeStats(response.data);
    } catch (error) {
      console.error('Failed to fetch size stats:', error);
    }
  };

  const fetchAspectRatioStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/datasets/${id}/aspect-ratio-stats`);
      const stats = Object.entries(response.data).map(([ratio, count]) => ({
        aspect_ratio: ratio,
        count: count as number,
      }));
      stats.sort((a, b) => b.count - a.count);
      setAspectRatioStats(stats);
    } catch (error) {
      console.error('Failed to fetch aspect ratio stats:', error);
    }
  };

  const fetchCaptionFilters = async () => {
    try {
      const response = await axios.get(`${API_URL}/datasets/${id}/caption-filters`);
      setCaptionFilters(response.data);
      // If the backend is still computing precomputed stats, re-fetch once it's done.
      if (response.data?.status === 'computing') {
        const interval = window.setInterval(async () => {
          try {
            const status = await axios.get(`${API_URL}/admin/filter-stats/${id}`);
            if (!status.data.isRunning && status.data.statsCount > 0) {
              window.clearInterval(interval);
              const fresh = await axios.get(`${API_URL}/datasets/${id}/caption-filters`);
              setCaptionFilters(fresh.data);
            }
          } catch (_) {
            // keep polling
          }
        }, 3000);
      }
    } catch (error) {
      console.error('Failed to fetch caption filters:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const dsType = dataset?.type || 'Photo';
      const [approveRes, declineRes] = await Promise.all([
        axios.get(`${API_URL}/tags?type=approve&dataset_type=${encodeURIComponent(dsType)}`),
        axios.get(`${API_URL}/tags?type=decline&dataset_type=${encodeURIComponent(dsType)}`),
      ]);
      setAllApproveTags(approveRes.data);
      setAllDeclineTags(declineRes.data);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const fetchJsonForImage = async (imageId: number) => {
    setJsonLoading(true);
    try {
      const response = await axios.get(`${API_URL}/images/${imageId}/caption-json`);
      setJsonData(response.data.caption_json);
    } catch (error) {
      console.error('Failed to fetch JSON data:', error);
      setJsonData(null);
    } finally {
      setJsonLoading(false);
    }
  };

  const fetchImages = async (cursorId: number | null, reset: boolean = false) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const params: any = { limit: 50 };
      if (cursorId) {
        params.cursor = cursorId;
      }
      if (selectedSizes.length > 0) {
        params.size = selectedSizes.join(',');
      }
      if (selectedAspectRatios.length > 0) {
        params.aspectRatio = selectedAspectRatios.join(',');
      }

      // Dropdown/multi-select filters
      for (const [key, values] of Object.entries(selectedCaptionFilters.dropdowns)) {
        if (values.length > 0) {
          params[key] = values.join(',');
          // Send exclude mode if set
          if (selectedCaptionFilters.modes[key] === 'exclude') {
            params[`${key}_exclude`] = 'true';
          }
        }
      }

      // Boolean filters
      for (const [key, value] of Object.entries(selectedCaptionFilters.booleans)) {
        if (value !== null && value !== undefined) {
          params[key] = value.toString();
          // For boolean flags, check if parent category is in exclude mode
          // Find the parent filter key for this boolean flag
          if (captionFilters) {
            for (const [filterKey, config] of Object.entries(captionFilters.filters)) {
              if (config.type === 'boolean_flags' && config.values?.some(v => v.value === key)) {
                if (selectedCaptionFilters.modes[filterKey] === 'exclude') {
                  params[`${key}_exclude`] = 'true';
                }
                break;
              }
            }
          }
        }
      }

      // Number range filters
      for (const [key, range] of Object.entries(selectedCaptionFilters.numbers)) {
        if (range) {
          if (range.min !== null) params[`${key}_min`] = range.min;
          if (range.max !== null) params[`${key}_max`] = range.max;
        }
      }

      // Text search filters
      for (const [key, value] of Object.entries(selectedCaptionFilters.text)) {
        if (value && value.length > 0) {
          params[key] = value;
        }
      }

      // Moderation status filter
      if (selectedModerationStatus.length > 0) {
        params.moderation_status = selectedModerationStatus.join(',');
      }

      // Tag filters
      if (selectedApproveTags.length > 0) {
        params.approve_tags = selectedApproveTags.join(',');
      }
      if (selectedDeclineTags.length > 0) {
        params.decline_tags = selectedDeclineTags.join(',');
      }

      const hasCaptionFilters = getCaptionFilterCount() > 0 || selectedModerationStatus.length > 0 || selectedApproveTags.length > 0 || selectedDeclineTags.length > 0;
      const endpoint = hasCaptionFilters
        ? `${API_URL}/datasets/${id}/images-with-captions`
        : `${API_URL}/datasets/${id}/images`;

      const response = await axios.get(endpoint, { params });

      const newImages = response.data.images;
      const paginationData = response.data.pagination;

      if (reset) {
        setImages(newImages);
      } else {
        setImages(prev => [...prev, ...newImages]);
      }

      setPagination(paginationData);
      setCursor(paginationData.cursor);
      setHasMore(paginationData.hasMore);
    } catch (error) {
      console.error('Failed to fetch images:', error);
      message.error('Failed to load images');
      // Don't set hasMore to false on error - keep previous value to allow retry
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore && cursor !== null) {
      fetchImages(cursor, false);
    }
  }, [loadingMore, hasMore, cursor]);

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loadingMore, loading, loadMore]);

  const getThumbnailUrl = (image: ImageData) => {
    const url = (image.size_category === 'extra_tiny' || image.size_category === 'tiny' || !image.thumbnail_uri)
      ? image.s3_url
      : image.thumbnail_uri;
    if (!url) return '';
    return `${url}${url.includes('?') ? '&' : '?'}id=${image.id}`;
  };

  // Filter handlers
  const handleSizeFilterChange = (size: string) => {
    setSelectedSizes(prev =>
      prev.includes(size) ? prev.filter(s => s !== size) : [...prev, size]
    );
  };

  const handleAspectRatioFilterChange = (ratio: string) => {
    setSelectedAspectRatios(prev =>
      prev.includes(ratio) ? prev.filter(r => r !== ratio) : [...prev, ratio]
    );
  };

  const clearFilters = () => {
    setSelectedSizes([]);
    setSelectedAspectRatios([]);
    setSelectedModerationStatus([]);
    setSelectedApproveTags([]);
    setSelectedDeclineTags([]);
    setSelectedCaptionFilters({
      dropdowns: {},
      booleans: {},
      numbers: {},
      text: {},
      modes: {}
    });
  };

  const handleModerationStatusChange = (status: string) => {
    setSelectedModerationStatus(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const handleApproveTagChange = (tag: string) => {
    setSelectedApproveTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const handleDeclineTagChange = (tag: string) => {
    setSelectedDeclineTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const handleCaptionFilterChange = (filterKey: string, value: string) => {
    setSelectedCaptionFilters(prev => {
      const current = prev.dropdowns[filterKey] || [];
      if (current.includes(value)) {
        return { ...prev, dropdowns: { ...prev.dropdowns, [filterKey]: current.filter(v => v !== value) } };
      } else {
        return { ...prev, dropdowns: { ...prev.dropdowns, [filterKey]: [...current, value] } };
      }
    });
  };

  const handleBooleanFilterChange = (filterKey: string, value: boolean | null) => {
    setSelectedCaptionFilters(prev => ({
      ...prev,
      booleans: { ...prev.booleans, [filterKey]: prev.booleans[filterKey] === value ? null : value }
    }));
  };

  const handleNumberFilterChange = (filterKey: string, type: 'min' | 'max', value: number | null) => {
    setSelectedCaptionFilters(prev => ({
      ...prev,
      numbers: {
        ...prev.numbers,
        [filterKey]: { ...(prev.numbers[filterKey] || { min: null, max: null }), [type]: value }
      }
    }));
  };

  const handleTextFilterChange = (filterKey: string, value: string) => {
    setSelectedCaptionFilters(prev => ({
      ...prev,
      text: { ...prev.text, [filterKey]: value }
    }));
  };

  const handleFilterModeChange = (filterKey: string, mode: FilterMode) => {
    setSelectedCaptionFilters(prev => ({
      ...prev,
      modes: { ...prev.modes, [filterKey]: mode }
    }));
  };

  const getCaptionFilterCount = () => {
    const dropdownCount = Object.values(selectedCaptionFilters.dropdowns).reduce((sum, arr) => sum + arr.length, 0);
    const booleanCount = Object.values(selectedCaptionFilters.booleans).filter(v => v !== null && v !== undefined).length;
    const numberCount = Object.values(selectedCaptionFilters.numbers).filter(v => v && (v.min !== null || v.max !== null)).length;
    const textCount = Object.values(selectedCaptionFilters.text).filter(v => v && v.length > 0).length;
    return dropdownCount + booleanCount + numberCount + textCount;
  };

  // Selection handlers
  const toggleImageSelection = (imageId: number) => {
    setSelectedImages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(imageId)) {
        newSet.delete(imageId);
      } else {
        newSet.add(imageId);
      }
      return newSet;
    });
  };

  const selectAllVisible = () => {
    const allIds = new Set(images.map(img => img.id));
    setSelectedImages(allIds);
  };

  const clearSelection = () => {
    setSelectedImages(new Set());
  };

  const toggleSelectionMode = () => {
    if (selectionMode) {
      clearSelection();
    }
    setSelectionMode(!selectionMode);
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedImages.size === 0) return;

    setDeleting(true);
    try {
      await axios.post(`${API_URL}/admin/images/bulk-delete`, {
        imageIds: Array.from(selectedImages)
      });
      message.success(`Deleted ${selectedImages.size} images`);
      clearSelection();
      resetAndFetch();
    } catch (error) {
      console.error('Failed to delete images:', error);
      message.error('Failed to delete images');
    } finally {
      setDeleting(false);
    }
  };

  // Clear cache and refresh
  const handleClearCache = async () => {
    setRefreshingCache(true);
    try {
      const response = await axios.post(`${API_URL}/datasets/${id}/clear-cache`);
      message.success(`Cache cleared (${response.data.clearedKeys} keys)`);
      // Refetch data after clearing cache
      resetAndFetch();
      fetchCaptionFilters();
    } catch (error) {
      console.error('Failed to clear cache:', error);
      message.error('Failed to clear cache');
    } finally {
      setRefreshingCache(false);
    }
  };

  // Fullscreen viewer handlers
  const openFullscreenViewer = (image: ImageData) => {
    setFullscreenImage(image);
  };

  const handleNextImage = () => {
    if (!fullscreenImage) return;
    const currentIndex = images.findIndex(img => img.id === fullscreenImage.id);
    if (currentIndex < images.length - 1) {
      setFullscreenImage(images[currentIndex + 1]);
    }
  };

  const handlePrevImage = () => {
    if (!fullscreenImage) return;
    const currentIndex = images.findIndex(img => img.id === fullscreenImage.id);
    if (currentIndex > 0) {
      setFullscreenImage(images[currentIndex - 1]);
    }
  };

  const hasNextImage = () => {
    if (!fullscreenImage) return false;
    const currentIndex = images.findIndex(img => img.id === fullscreenImage.id);
    return currentIndex < images.length - 1;
  };

  const hasPrevImage = () => {
    if (!fullscreenImage) return false;
    const currentIndex = images.findIndex(img => img.id === fullscreenImage.id);
    return currentIndex > 0;
  };

  // Caption editor handlers
  const handleImageClick = (imageId: number) => {
    if (selectionMode) {
      toggleImageSelection(imageId);
    } else {
      const image = images.find(img => img.id === imageId);
      if (image) {
        openFullscreenViewer(image);
      }
    }
  };

  // Right-click to show JSON in right panel
  const handleImageRightClick = (e: React.MouseEvent, imageId: number) => {
    e.preventDefault();
    const image = images.find(img => img.id === imageId);
    if (image) {
      setSelectedImageForJson(image);
      setRightPanelMode('json');
      setFilterPanelOpen(true);
      fetchJsonForImage(imageId);
    }
  };

  // Right panel resize handlers
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeRef.current = { startX: e.clientX, startWidth: rightPanelWidth };
  };

  useEffect(() => {
    const handleResizeMove = (e: MouseEvent) => {
      if (!isResizing || !resizeRef.current) return;
      const delta = resizeRef.current.startX - e.clientX;
      const newWidth = Math.max(200, Math.min(600, resizeRef.current.startWidth + delta));
      setRightPanelWidth(newWidth);
    };

    const handleResizeEnd = () => {
      setIsResizing(false);
      resizeRef.current = null;
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isResizing]);

  const handleCaptionSave = () => {
    setEditingImageId(null);
    // Optionally refresh the current image in the list
  };

  const formatCount = (count: number) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  };

  const toggleFilterSection = (key: string) => {
    setExpandedFilters(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  if (!dataset) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const activeFiltersCount = selectedSizes.length + selectedAspectRatios.length + getCaptionFilterCount() + selectedModerationStatus.length + selectedApproveTags.length + selectedDeclineTags.length;

  // Filter Section Component
  const FilterSection = ({ title, sectionKey, children, selectedCount = 0, showModeToggle = false, filterKey }: {
    title: string;
    sectionKey: string;
    children: React.ReactNode;
    selectedCount?: number;
    showModeToggle?: boolean;
    filterKey?: string;
  }) => {
    const isExpanded = expandedFilters.includes(sectionKey);
    const mode = filterKey ? (selectedCaptionFilters.modes[filterKey] || 'include') : 'include';
    const isExcludeMode = mode === 'exclude';

    return (
      <div style={{ marginBottom: '4px' }}>
        <div
          onClick={() => toggleFilterSection(sectionKey)}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px',
            cursor: 'pointer',
            backgroundColor: isExcludeMode && selectedCount > 0 ? '#fff1f0' : '#fafafa',
            borderRadius: '4px',
            marginBottom: isExpanded ? '4px' : 0,
            borderLeft: isExcludeMode && selectedCount > 0 ? '3px solid #ff4d4f' : 'none',
          }}
        >
          <Text strong style={{ fontSize: '12px' }}>{title}</Text>
          <Space size={4}>
            {selectedCount > 0 && (
              <Badge
                count={isExcludeMode ? `-${selectedCount}` : selectedCount}
                size="small"
                style={{ backgroundColor: isExcludeMode ? '#ff4d4f' : '#6366f1' }}
              />
            )}
            {isExpanded ? <DownOutlined style={{ fontSize: '10px' }} /> : <RightOutlined style={{ fontSize: '10px' }} />}
          </Space>
        </div>
        {isExpanded && (
          <div style={{ padding: '4px 0', maxHeight: '200px', overflowY: 'auto' }}>
            {showModeToggle && filterKey && (
              <div style={{
                padding: '4px 12px 8px',
                borderBottom: '1px solid #f0f0f0',
                marginBottom: '4px'
              }}>
                <Space size={4}>
                  <Button
                    size="small"
                    type={mode === 'include' ? 'primary' : 'default'}
                    icon={<PlusCircleOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFilterModeChange(filterKey, 'include');
                    }}
                    style={{ fontSize: '11px', padding: '0 8px' }}
                  >
                    Include
                  </Button>
                  <Button
                    size="small"
                    type={mode === 'exclude' ? 'primary' : 'default'}
                    danger={mode === 'exclude'}
                    icon={<MinusCircleOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFilterModeChange(filterKey, 'exclude');
                    }}
                    style={{ fontSize: '11px', padding: '0 8px' }}
                  >
                    Exclude
                  </Button>
                </Space>
              </div>
            )}
            {children}
          </div>
        )}
      </div>
    );
  };

  // Filter Item Component
  const FilterItem = ({ label, count, selected, onClick, hideCount }: {
    label: string;
    count: number;
    selected: boolean;
    onClick: () => void;
    hideCount?: boolean;
  }) => (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '4px 12px',
        cursor: 'pointer',
        backgroundColor: selected ? '#e6f4ff' : 'transparent',
        borderRadius: '4px',
        fontSize: '12px',
      }}
    >
      <Space size={6}>
        <Checkbox checked={selected} />
        <span>{label}</span>
      </Space>
      {!hideCount && <Text type="secondary" style={{ fontSize: '11px' }}>{formatCount(count)}</Text>}
    </div>
  );

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'row',
      height: 'calc(100vh - 48px)',
      margin: '-24px',
      overflow: 'hidden',
    }}>
      {/* Main Content Area */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minWidth: 0,
      }}>
        {/* Header */}
        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid #f0f0f0',
          backgroundColor: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}>
          <Space size="middle">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/datasets')}
              size="small"
            />
            <Space size={8}>
              <PictureOutlined style={{ color: '#6366f1', fontSize: '18px' }} />
              <Title level={5} style={{ margin: 0 }}>{dataset.name}</Title>
              <Tag color={dataset.type === 'Anime' ? 'purple' : 'green'} style={{ margin: 0 }}>
                {dataset.type || 'Photo'}
              </Tag>
            </Space>
          </Space>

          <Space size="small">
            {pagination && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {images.length} / {formatCount(pagination.total)} images
              </Text>
            )}

            {/* View Mode Toggle */}
            <Segmented
              size="small"
              value={viewMode}
              onChange={(v) => setViewMode(v as 'grid' | 'list')}
              options={[
                { value: 'grid', icon: <AppstoreOutlined /> },
                { value: 'list', icon: <UnorderedListOutlined /> },
              ]}
            />

            {/* Selection Mode Toggle */}
            <Button
              size="small"
              type={selectionMode ? 'primary' : 'default'}
              icon={selectionMode ? <CheckSquareOutlined /> : <BorderOutlined />}
              onClick={toggleSelectionMode}
            >
              {selectionMode ? 'Exit Select' : 'Select'}
            </Button>

            {/* Export Button */}
            <Button
              size="small"
              icon={<ExportOutlined />}
              onClick={() => setExportDialogOpen(true)}
            >
              Export
            </Button>

            {/* Refresh Cache Button */}
            <Tooltip title="Clear Redis cache and refresh data">
              <Button
                size="small"
                icon={<ReloadOutlined spin={refreshingCache} />}
                onClick={handleClearCache}
                loading={refreshingCache}
              >
                Refresh
              </Button>
            </Tooltip>

            {/* Filter Toggle */}
            <Tooltip title={filterPanelOpen ? 'Hide Filters' : 'Show Filters'}>
              <Button
                size="small"
                type={activeFiltersCount > 0 ? 'primary' : 'default'}
                icon={filterPanelOpen ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setFilterPanelOpen(!filterPanelOpen)}
              >
                {activeFiltersCount > 0 && !filterPanelOpen && (
                  <Badge count={activeFiltersCount} size="small" offset={[4, -4]} />
                )}
              </Button>
            </Tooltip>
          </Space>
        </div>

        {/* Bulk Actions Bar */}
        {selectionMode && selectedImages.size > 0 && (
          <div style={{
            padding: '8px 16px',
            backgroundColor: '#e6f4ff',
            borderBottom: '1px solid #91caff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}>
            <Space>
              <Text strong>{selectedImages.size} selected</Text>
              <Button size="small" onClick={selectAllVisible}>
                Select All Visible ({images.length})
              </Button>
              <Button size="small" onClick={clearSelection} icon={<CloseOutlined />}>
                Clear
              </Button>
            </Space>
            <Space>
              <Button
                size="small"
                icon={<ExportOutlined />}
                onClick={() => setExportDialogOpen(true)}
              >
                Export Selected
              </Button>
              <Popconfirm
                title={`Delete ${selectedImages.size} images?`}
                description="This action cannot be undone."
                onConfirm={handleBulkDelete}
                okText="Delete"
                okButtonProps={{ danger: true }}
              >
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deleting}
                >
                  Delete
                </Button>
              </Popconfirm>
            </Space>
          </div>
        )}

        {/* Images Container */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '16px',
          backgroundColor: '#f5f5f5',
        }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
              <Spin size="large" />
            </div>
          ) : images.length === 0 ? (
            <Card>
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <PictureOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                <Title level={5} style={{ marginTop: '12px' }}>No images found</Title>
                <Text type="secondary">
                  {activeFiltersCount > 0 ? 'Try adjusting filters.' : 'No images in selected buckets.'}
                </Text>
                {activeFiltersCount > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <Button size="small" onClick={clearFilters}>Clear Filters</Button>
                  </div>
                )}
              </div>
            </Card>
          ) : (
            <>
              <Row gutter={[8, 8]}>
                {images.map((image: ImageData) => (
                  <Col
                    xs={viewMode === 'list' ? 24 : 8}
                    sm={viewMode === 'list' ? 24 : 6}
                    md={viewMode === 'list' ? 24 : 4}
                    lg={viewMode === 'list' ? 24 : 3}
                    xl={viewMode === 'list' ? 24 : 3}
                    xxl={viewMode === 'list' ? 24 : 2}
                    key={image.id}
                  >
                    <Card
                      hoverable
                      size="small"
                      bodyStyle={{ padding: viewMode === 'list' ? '8px 12px' : '4px 6px' }}
                      onClick={() => handleImageClick(image.id)}
                      onContextMenu={(e) => handleImageRightClick(e, image.id)}
                      style={{
                        border: selectedImages.has(image.id) ? '2px solid #6366f1' :
                               (selectedImageForJson?.id === image.id && rightPanelMode === 'json') ? '2px solid #52c41a' : undefined,
                      }}
                      cover={viewMode === 'grid' ? (
                        <div style={{ position: 'relative', overflow: 'hidden' }}>
                          {selectionMode && (
                            <div style={{
                              position: 'absolute',
                              top: 4,
                              left: 4,
                              zIndex: 10,
                            }}>
                              <Checkbox
                                checked={selectedImages.has(image.id)}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => toggleImageSelection(image.id)}
                              />
                            </div>
                          )}
                          <Image
                            key={`thumb-${image.id}`}
                            src={getThumbnailUrl(image)}
                            alt={`Image ${image.id}`}
                            style={{
                              width: '100%',
                              height: '100px',
                              objectFit: 'cover',
                              transform: image.rotation ? `rotate(${image.rotation}deg)` : undefined,
                            }}
                            preview={false}
                            fallback="data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100'><rect fill='%23f0f0f0' width='100' height='100'/></svg>"
                          />
                          {image.caption_summary && (
                            <div style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              background: 'rgba(0,0,0,0.85)',
                              color: '#fff',
                              padding: '4px',
                              fontSize: '9px',
                              lineHeight: 1.2,
                              maxHeight: '36px',
                              overflow: 'hidden',
                            }}>
                              {image.caption_summary}
                            </div>
                          )}
                        </div>
                      ) : undefined}
                    >
                      {viewMode === 'list' ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          {selectionMode && (
                            <Checkbox
                              checked={selectedImages.has(image.id)}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggleImageSelection(image.id)}
                            />
                          )}
                          <Image
                            key={`list-${image.id}`}
                            src={getThumbnailUrl(image)}
                            alt={`Image ${image.id}`}
                            style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                            preview={false}
                          />
                          <div style={{ flex: 1 }}>
                            <Text strong>ID: {image.id}</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {image.original_width}×{image.original_height} · {image.size_category}
                            </Text>
                            {image.caption_summary && (
                              <>
                                <br />
                                <Text style={{ fontSize: '11px' }}>{image.caption_summary}</Text>
                              </>
                            )}
                          </div>
                          <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingImageId(image.id);
                            }}
                          >
                            Edit
                          </Button>
                        </div>
                      ) : (
                        <Text type="secondary" style={{ fontSize: '9px' }}>
                          {image.original_width}×{image.original_height}
                        </Text>
                      )}
                    </Card>
                  </Col>
                ))}
              </Row>

              <div ref={loadMoreRef} style={{ padding: '20px', textAlign: 'center' }}>
                {loadingMore && (
                  <Space>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 20 }} spin />} />
                    <Text type="secondary" style={{ fontSize: '12px' }}>Loading...</Text>
                  </Space>
                )}
                {!hasMore && images.length > 0 && (
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '4px' }} />
                    All {formatCount(pagination?.total || 0)} loaded
                  </Text>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Right Panel (Filters or JSON) */}
      <div style={{
        width: filterPanelOpen ? rightPanelWidth : 0,
        flexShrink: 0,
        backgroundColor: '#fff',
        transition: isResizing ? 'none' : 'width 0.2s ease',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'row',
        position: 'relative',
      }}>
        {/* Resize Handle */}
        {filterPanelOpen && (
          <div
            onMouseDown={handleResizeStart}
            style={{
              width: 4,
              cursor: 'col-resize',
              backgroundColor: isResizing ? '#6366f1' : 'transparent',
              borderLeft: '1px solid #f0f0f0',
              flexShrink: 0,
              transition: 'background-color 0.15s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#e0e0e0')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isResizing ? '#6366f1' : 'transparent')}
          />
        )}
        {filterPanelOpen && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Panel Header with Mode Toggle */}
            <div style={{
              padding: '8px 12px',
              borderBottom: '1px solid #f0f0f0',
              flexShrink: 0,
            }}>
              <Segmented
                block
                size="small"
                value={rightPanelMode}
                onChange={(v) => setRightPanelMode(v as 'filters' | 'json')}
                options={[
                  { value: 'filters', label: 'Filters', icon: <FilterOutlined /> },
                  { value: 'json', label: 'Raw JSON', icon: <CodeOutlined /> },
                ]}
              />
            </div>

            {rightPanelMode === 'filters' ? (
              <>
                {/* Filter Header */}
                <div style={{
                  padding: '8px 12px',
                  borderBottom: '1px solid #f0f0f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexShrink: 0,
                }}>
                  <Space size={4}>
                    <Text strong style={{ fontSize: '13px' }}>Filters</Text>
                    {activeFiltersCount > 0 && (
                      <Badge count={activeFiltersCount} size="small" style={{ backgroundColor: '#6366f1' }} />
                    )}
                  </Space>
                  {activeFiltersCount > 0 && (
                    <Button type="link" size="small" onClick={clearFilters} style={{ padding: 0, fontSize: '11px' }}>
                      Clear
                    </Button>
                  )}
                </div>

                {/* Filter Content */}
                <div style={{ flex: 1, overflow: 'auto', padding: '8px' }}>
              {/* Size Filter */}
              <FilterSection title="Size" sectionKey="size" selectedCount={selectedSizes.length}>
                {SIZE_CATEGORIES.map(cat => {
                  const count = sizeStats[cat.value] || 0;
                  if (count === 0) return null;
                  return (
                    <FilterItem
                      key={cat.value}
                      label={cat.label}
                      count={count}
                      selected={selectedSizes.includes(cat.value)}
                      onClick={() => handleSizeFilterChange(cat.value)}
                    />
                  );
                })}
              </FilterSection>

              {/* Aspect Ratio Filter */}
              <FilterSection title="Aspect Ratio" sectionKey="ratio" selectedCount={selectedAspectRatios.length}>
                {aspectRatioStats.map(stat => (
                  <FilterItem
                    key={stat.aspect_ratio}
                    label={stat.aspect_ratio}
                    count={stat.count}
                    selected={selectedAspectRatios.includes(stat.aspect_ratio)}
                    onClick={() => handleAspectRatioFilterChange(stat.aspect_ratio)}
                  />
                ))}
              </FilterSection>

              {/* Moderation Status Filter */}
              <FilterSection title="Moderation" sectionKey="moderation" selectedCount={selectedModerationStatus.length}>
                <FilterItem
                  label="Approved"
                  count={0}
                  selected={selectedModerationStatus.includes('approved')}
                  onClick={() => handleModerationStatusChange('approved')}
                  hideCount
                />
                <FilterItem
                  label="Declined"
                  count={0}
                  selected={selectedModerationStatus.includes('declined')}
                  onClick={() => handleModerationStatusChange('declined')}
                  hideCount
                />
              </FilterSection>

              {/* Approve Tags Filter */}
              {allApproveTags.length > 0 && (
                <FilterSection title="Approve Tags" sectionKey="approve_tags" selectedCount={selectedApproveTags.length}>
                  {allApproveTags.map(tag => (
                    <FilterItem
                      key={tag}
                      label={tag}
                      count={0}
                      selected={selectedApproveTags.includes(tag)}
                      onClick={() => handleApproveTagChange(tag)}
                      hideCount
                    />
                  ))}
                </FilterSection>
              )}

              {/* Decline Tags Filter */}
              {allDeclineTags.length > 0 && (
                <FilterSection title="Decline Tags" sectionKey="decline_tags" selectedCount={selectedDeclineTags.length}>
                  {allDeclineTags.map(tag => (
                    <FilterItem
                      key={tag}
                      label={tag}
                      count={0}
                      selected={selectedDeclineTags.includes(tag)}
                      onClick={() => handleDeclineTagChange(tag)}
                      hideCount
                    />
                  ))}
                </FilterSection>
              )}

              {/* Caption Filters */}
              {captionFilters && Object.entries(captionFilters.filters).map(([filterKey, filterConfig]) => {
                const filterType = filterConfig.type;

                // Boolean filter
                if (filterType === 'boolean') {
                  const counts = filterConfig.counts || {};
                  const trueCount = counts['true'] || 0;
                  const falseCount = counts['false'] || 0;
                  if (trueCount === 0 && falseCount === 0) return null;

                  const currentValue = selectedCaptionFilters.booleans[filterKey];
                  const selectedCount = currentValue !== null && currentValue !== undefined ? 1 : 0;

                  return (
                    <FilterSection key={filterKey} title={filterConfig.label} sectionKey={filterKey} selectedCount={selectedCount}>
                      {trueCount > 0 && (
                        <FilterItem
                          label="Yes"
                          count={trueCount}
                          selected={currentValue === true}
                          onClick={() => handleBooleanFilterChange(filterKey, true)}
                        />
                      )}
                      {falseCount > 0 && (
                        <FilterItem
                          label="No"
                          count={falseCount}
                          selected={currentValue === false}
                          onClick={() => handleBooleanFilterChange(filterKey, false)}
                        />
                      )}
                    </FilterSection>
                  );
                }

                // Boolean flags
                if (filterType === 'boolean_flags') {
                  const counts = filterConfig.counts || {};
                  const hasAnyCount = Object.values(counts).some(c => (c as number) > 0);
                  if (!hasAnyCount) return null;

                  const selectedFlags = Object.entries(selectedCaptionFilters.booleans)
                    .filter(([k, v]) => filterConfig.values?.some(opt => opt.value === k) && v === true)
                    .length;

                  return (
                    <FilterSection key={filterKey} title={filterConfig.label} sectionKey={filterKey} selectedCount={selectedFlags} showModeToggle={true} filterKey={filterKey}>
                      {filterConfig.values?.map(option => {
                        const count = counts[option.value] || 0;
                        if (count === 0) return null;
                        const isSelected = selectedCaptionFilters.booleans[option.value] === true;
                        return (
                          <FilterItem
                            key={option.value}
                            label={option.label}
                            count={count as number}
                            selected={isSelected}
                            onClick={() => handleBooleanFilterChange(option.value, true)}
                          />
                        );
                      })}
                    </FilterSection>
                  );
                }

                // Number range filter
                if (filterType === 'min_max') {
                  const config = filterConfig as any;
                  if (config.total === 0) return null;

                  const currentRange = selectedCaptionFilters.numbers[filterKey];
                  const selectedCount = currentRange && (currentRange.min !== null || currentRange.max !== null) ? 1 : 0;

                  return (
                    <FilterSection key={filterKey} title={filterConfig.label} sectionKey={filterKey} selectedCount={selectedCount}>
                      <div style={{ padding: '4px 12px' }}>
                        <Space size={8}>
                          <InputNumber
                            size="small"
                            placeholder="Min"
                            min={config.min || 0}
                            max={config.max || 100}
                            value={currentRange?.min}
                            onChange={(v) => handleNumberFilterChange(filterKey, 'min', v)}
                            style={{ width: 70 }}
                          />
                          <span>-</span>
                          <InputNumber
                            size="small"
                            placeholder="Max"
                            min={config.min || 0}
                            max={config.max || 100}
                            value={currentRange?.max}
                            onChange={(v) => handleNumberFilterChange(filterKey, 'max', v)}
                            style={{ width: 70 }}
                          />
                        </Space>
                        <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginTop: 4 }}>
                          Total: {formatCount(config.total || 0)}
                        </Text>
                      </div>
                    </FilterSection>
                  );
                }

                // Text search filter
                if (filterType === 'text_search') {
                  const currentText = selectedCaptionFilters.text[filterKey] || '';
                  const selectedCount = currentText.length > 0 ? 1 : 0;

                  return (
                    <FilterSection key={filterKey} title={filterConfig.label} sectionKey={filterKey} selectedCount={selectedCount}>
                      <div style={{ padding: '4px 12px' }}>
                        <Input.Search
                          size="small"
                          placeholder={`Search ${filterConfig.label.toLowerCase()}...`}
                          value={currentText}
                          onChange={(e) => handleTextFilterChange(filterKey, e.target.value)}
                          onSearch={(v) => handleTextFilterChange(filterKey, v)}
                          allowClear
                          style={{ width: '100%' }}
                        />
                      </div>
                    </FilterSection>
                  );
                }

                // Dropdown filter (default)
                const hasValues = filterConfig.values && Object.values(filterConfig.counts || {}).some(c => (c as number) > 0);
                if (!hasValues) return null;

                const selectedCount = (selectedCaptionFilters.dropdowns[filterKey] || []).length;

                return (
                  <FilterSection
                    key={filterKey}
                    title={filterConfig.label}
                    sectionKey={filterKey}
                    selectedCount={selectedCount}
                    showModeToggle={true}
                    filterKey={filterKey}
                  >
                    {filterConfig.values?.map(option => {
                      const count = filterConfig.counts[option.value] || 0;
                      if (count === 0) return null;
                      const isSelected = (selectedCaptionFilters.dropdowns[filterKey] || []).includes(option.value);
                      return (
                        <FilterItem
                          key={option.value}
                          label={option.label}
                          count={count as number}
                          selected={isSelected}
                          onClick={() => handleCaptionFilterChange(filterKey, option.value)}
                        />
                      );
                    })}
                  </FilterSection>
                );
              })}
                </div>
              </>
            ) : (
              /* JSON Panel */
              <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                {selectedImageForJson ? (
                  <>
                    {/* Image Info Header */}
                    <div style={{
                      padding: '8px 12px',
                      borderBottom: '1px solid #f0f0f0',
                      backgroundColor: '#fafafa',
                    }}>
                      <Space size={8}>
                        <img
                          src={getThumbnailUrl(selectedImageForJson)}
                          alt={`ID ${selectedImageForJson.id}`}
                          style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                        />
                        <div>
                          <Text strong>ID: {selectedImageForJson.id}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {selectedImageForJson.original_width}×{selectedImageForJson.original_height}
                          </Text>
                        </div>
                      </Space>
                    </div>

                    {/* JSON Content */}
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                      {jsonLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                          <Spin />
                        </div>
                      ) : jsonData ? (
                        <pre style={{
                          margin: 0,
                          padding: 12,
                          fontSize: 11,
                          fontFamily: 'Monaco, Menlo, monospace',
                          backgroundColor: '#1e1e1e',
                          color: '#d4d4d4',
                          overflow: 'auto',
                          height: '100%',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}>
                          {JSON.stringify(jsonData, null, 2)}
                        </pre>
                      ) : (
                        <div style={{ textAlign: 'center', padding: 40 }}>
                          <Text type="secondary">No JSON data available</Text>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200, padding: 20 }}>
                    <Text type="secondary" style={{ textAlign: 'center' }}>
                      Right-click on an image to view its raw JSON data
                    </Text>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fullscreen Viewer */}
      <FullscreenViewer
        visible={fullscreenImage !== null}
        image={fullscreenImage}
        onClose={() => setFullscreenImage(null)}
        onNext={handleNextImage}
        onPrev={handlePrevImage}
        hasNext={hasNextImage()}
        hasPrev={hasPrevImage()}
      />

      {/* Caption Editor Drawer */}
      <CaptionEditor
        imageId={editingImageId}
        visible={editingImageId !== null}
        onClose={() => setEditingImageId(null)}
        onSave={handleCaptionSave}
      />

      {/* Export Dialog */}
      <ExportDialog
        visible={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        datasetId={parseInt(id || '0')}
        datasetName={dataset?.name}
        selectedImageIds={selectionMode ? Array.from(selectedImages) : undefined}
        filters={{
          sizes: selectedSizes,
          aspectRatios: selectedAspectRatios,
          captionFilters: selectedCaptionFilters
        }}
      />
    </div>
  );
};

export default DatasetManager;
